import { useEffect, useState, Component, type ReactNode } from 'react'
import { isAuthenticated, handleCallback, login } from './auth'
import NavBar, { type Page } from './components/NavBar'
import Dashboard from './pages/Dashboard'
import Worlds from './pages/Worlds'
import Players from './pages/Players'
import Packs from './pages/Packs'
import Update from './pages/Update'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      const err = this.state.error as Error
      return (
        <div style={{ padding: 32, fontFamily: 'monospace' }}>
          <h2 style={{ color: '#ef4444' }}>Something went wrong</h2>
          <pre style={{ background: '#1e1e2e', color: '#cdd6f4', padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 12 }}>
            {err.message}{'\n\n'}{err.stack}
          </pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px' }}>
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  const [ready, setReady]       = useState(false)
  const [authErr, setAuthErr]   = useState(false)
  const [page, setPage]         = useState<Page>('dashboard')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code   = params.get('code')
    const error  = params.get('error')

    if (error) {
      setAuthErr(true)
      return
    }

    if (code) {
      handleCallback(code).then(ok => {
        if (ok) {
          window.history.replaceState({}, '', '/')
          setReady(true)
        } else {
          setAuthErr(true)
        }
      })
      return
    }

    if (isAuthenticated()) {
      setReady(true)
    } else {
      login()
    }
  }, [])

  if (authErr) {
    return (
      <div className="loading" style={{ flexDirection: 'column', gap: 16 }}>
        <p>Authentication failed.</p>
        <button className="btn btn-primary" onClick={login}>Try again</button>
      </div>
    )
  }

  if (!ready) {
    return (
      <div className="loading">
        <span className="spinning">◌</span> Authenticating…
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="app">
        <NavBar page={page} onNavigate={setPage} />
        <main>
          {page === 'dashboard' && <Dashboard onSwitchWorld={() => setPage('worlds')} />}
          {page === 'worlds'    && <Worlds />}
          {page === 'players'   && <Players />}
          {page === 'packs'     && <Packs />}
          {page === 'update'    && <Update />}
        </main>
      </div>
    </ErrorBoundary>
  )
}
